import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { api } from "@/lib/api";

const BASE = process.env.REACT_APP_BACKEND_URL;

const Viewer = ({ photos, index, onClose, onNav }) => {
  const p = photos[index];
  useEffect(() => {
    const k = (e) => { if (e.key === "Escape") onClose(); if (e.key === "ArrowRight") onNav(1); if (e.key === "ArrowLeft") onNav(-1); };
    window.addEventListener("keydown", k); return () => window.removeEventListener("keydown", k);
  }, [onClose, onNav]);
  return (
    <motion.div className="fixed inset-0 z-50 bg-[#0F2338]/95 flex flex-col" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose} data-testid="gallery-viewer">
      <button className="absolute top-4 right-4 text-white p-2" onClick={onClose} aria-label="Fermer" data-testid="gallery-viewer-close"><X size={26} /></button>
      <button className="absolute left-2 top-1/2 -translate-y-1/2 text-white p-2 hidden sm:block" onClick={(e) => { e.stopPropagation(); onNav(-1); }} aria-label="Précédente"><ChevronLeft size={32} /></button>
      <button className="absolute right-2 top-1/2 -translate-y-1/2 text-white p-2 hidden sm:block" onClick={(e) => { e.stopPropagation(); onNav(1); }} aria-label="Suivante"><ChevronRight size={32} /></button>
      <div className="flex-1 flex items-center justify-center p-4 md:p-12" onClick={(e) => e.stopPropagation()}>
        <motion.img key={p.id} src={`${BASE}${p.full_url}`} alt={p.legende} className="max-w-full max-h-[78vh] object-contain rounded-[4px]" initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.35 }} />
      </div>
      <div className="px-6 pb-6 text-white font-body-l">
        <div className="font-display text-base md:text-lg font-bold">{p.legende || p.metier || "Chantier"}</div>
        <div className="text-xs text-white/60 mt-1">{[p.raison_sociale, p.ville].filter(Boolean).join(" · ")} <span className="font-mono-l ml-3">{index + 1}/{photos.length}</span></div>
      </div>
    </motion.div>
  );
};

export const Gallery = () => {
  const [photos, setPhotos] = useState([]);
  const [open, setOpen] = useState(null);
  useEffect(() => { api.get("/public/gallery").then((r) => setPhotos(r.data.photos || [])).catch(() => {}); }, []);
  if (!photos.length) return null;
  const nav = (d) => setOpen((i) => (i + d + photos.length) % photos.length);
  return (
    <section className="relative bg-white" data-testid="gallery-section">
      <div className="max-w-6xl mx-auto px-5 md:px-8 py-20 md:py-32">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
          <div>
            <span className="font-mono-l text-[11px] uppercase tracking-[0.24em] font-bold text-[#E8710A]">Chantiers réels</span>
            <h2 className="font-display mt-3 text-2xl sm:text-3xl lg:text-5xl font-semibold tracking-[-0.02em] text-[#0F2338] leading-[1.05]">Le travail de nos artisans.</h2>
          </div>
          <p className="font-body-l text-sm text-[#475569] max-w-sm">Photos publiées par les artisans eux-mêmes sur leur fiche, avec leur accord.</p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5 md:gap-4">
          {photos.map((p, i) => (
            <motion.figure key={p.id} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.6, delay: (i % 4) * 0.08 }}
              className={`relative overflow-hidden rounded-[6px] cursor-zoom-in group ${i % 5 === 0 ? "col-span-2 row-span-2" : ""}`} onClick={() => setOpen(i)} data-testid={`gallery-card-${p.id}`}>
              <img src={`${BASE}${p.thumb_url}`} alt={p.legende} loading="lazy" className="w-full h-full min-h-[140px] md:min-h-[200px] object-cover group-hover:scale-[1.04] transition-transform duration-700" />
              <figcaption className="absolute inset-x-0 bottom-0 p-3 bg-gradient-to-t from-[#0F2338]/85 to-transparent text-white">
                <div className="font-display text-xs md:text-sm font-bold truncate">{p.legende || p.metier || "Chantier"}</div>
                <div className="text-[10px] text-white/70 truncate font-body-l">{p.raison_sociale}</div>
              </figcaption>
            </motion.figure>
          ))}
        </div>
      </div>
      <AnimatePresence>{open !== null && <Viewer photos={photos} index={open} onClose={() => setOpen(null)} onNav={nav} />}</AnimatePresence>
    </section>
  );
};
